import { useEffect, useMemo, useState } from 'react'

import { Grid } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { ConfirmDistanceChangeDialog } from 'components/bookings/common'
import { ControlledNumericFormat } from 'components/common'
import { DEBOUNCE_DELAY, Mode } from 'constants/index'
import { useGetBookingTariffQuery } from 'generated/graphql'
import { calcBookingConsignmentFee } from 'helpers'
import { Restricted, usePermission } from 'providers'
import { TBookingForm } from 'types'

interface IConsignmentFeeFromTariffProps {
  mode?: number
}

export const ConsignmentFeeFromTariff = (_props: IConsignmentFeeFromTariffProps) => {
  const { mode } = _props

  const {
    control,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
  } = useFormContext<TBookingForm>()

  const [openConfirmDistanceChangeDialog, setOpenConfirmDistanceChangeDialog] = useState(false)

  const [distanceValue, returnDistanceValue, tariffIdValue, isWaitAndReturn] = useWatch({
    name: ['distance', 'returnDistance', 'tariffId', 'isWaitAndReturn'],
  }) as [number, number, any, boolean]
  const [distanceValueDebounced] = useDebounce(distanceValue, DEBOUNCE_DELAY)
  const [returnDistanceValueDebounced] = useDebounce(returnDistanceValue, DEBOUNCE_DELAY)

  const [isAllowedToShowConfirmationDialogWhenDockedIsRePriced] = usePermission(
    'ShowConfirmationDialogWhenDockedIsRePriced',
  )

  const { data: tariffData } = useGetBookingTariffQuery({
    variables: { id: Number(tariffIdValue) },
    skip: !tariffIdValue,
  })

  const calculatedConsignmentFeeFromTariff = useMemo(() => {
    if (!tariffData?.tariffById || !distanceValueDebounced) {
      return 0
    }

    if (isWaitAndReturn && !returnDistanceValueDebounced) {
      return 0
    }

    const result = calcBookingConsignmentFee(
      distanceValueDebounced - returnDistanceValueDebounced,
      tariffData.tariffById.minMiles,
      tariffData.tariffById.minCharge,
      tariffData.tariffById.rate,
    )

    return result
  }, [
    distanceValueDebounced,
    returnDistanceValueDebounced,
    isWaitAndReturn,
    tariffData?.tariffById,
  ])

  // Subscriptions
  useEffect(() => {
    const currentConsignmentFeeFromTariff = getValues('consignmentFeeFromTariff') ?? 0

    if (calculatedConsignmentFeeFromTariff === currentConsignmentFeeFromTariff) {
      return
    }

    if (mode === Mode.Create) {
      setValue('consignmentFeeFromTariff', calculatedConsignmentFeeFromTariff, {
        shouldDirty: true,
      })
      return
    }

    if (mode === Mode.Update) {
      const isDistanceDirty = !!dirtyFields.distance
      const isReturnDistanceDirty = !!dirtyFields.returnDistance
      const isTariffDirty = !!dirtyFields.tariffId
      const isConsignmentFeeFromTariffDirty = !!dirtyFields.consignmentFeeFromTariff

      const shouldRecalculate =
        (isDistanceDirty ||
          isReturnDistanceDirty ||
          isTariffDirty ||
          isConsignmentFeeFromTariffDirty) &&
        (!isWaitAndReturn || !!returnDistanceValueDebounced)

      if (!shouldRecalculate) {
        return
      }

      if (isAllowedToShowConfirmationDialogWhenDockedIsRePriced) {
        setOpenConfirmDistanceChangeDialog(true)
      } else {
        setValue('consignmentFeeFromTariff', calculatedConsignmentFeeFromTariff, {
          shouldDirty: true,
        })
      }
    }
  }, [
    calculatedConsignmentFeeFromTariff,
    getValues,
    isAllowedToShowConfirmationDialogWhenDockedIsRePriced,
    isWaitAndReturn,
    mode,
    returnDistanceValueDebounced,
    setValue,
    dirtyFields.distance,
    dirtyFields.returnDistance,
    dirtyFields.tariffId,
    dirtyFields.consignmentFeeFromTariff,
  ])

  const handleConfirmChangeDistance = () => {
    if (!calculatedConsignmentFeeFromTariff) {
      return
    }
    setValue('consignmentFeeFromTariff', calculatedConsignmentFeeFromTariff, { shouldDirty: true })
  }

  return (
    <>
      <Restricted to='ViewConsignmentFeeFromDynamicPricing'>
        <Grid item xs={12}>
          <ControlledNumericFormat
            control={control}
            name='consignmentFeeFromTariff'
            label='Consignment Fee From Tariff'
            InputProps={{
              readOnly: true,
            }}
            error={!!errors.consignmentFeeFromTariff}
            helperText={errors.consignmentFeeFromTariff?.message}
          />
        </Grid>
      </Restricted>

      <ConfirmDistanceChangeDialog
        open={openConfirmDistanceChangeDialog}
        setOpen={setOpenConfirmDistanceChangeDialog}
        onConfirm={handleConfirmChangeDistance}
      />
    </>
  )
}
